/*
 * st-addon-anchor.js — keeps discounted "Add On" lines anchored to a full-price line.
 *
 * THE RULE: an Add On variant is only valid while at least one non-Add-On line
 * (a "Default Title" line, or any other real variant) sits in the same cart.
 * The VARIANT TITLE is the marker, never the price, so admin price changes and
 * automatic discounts (e.g. the CB1 Upgrade £20 off) can never confuse it.
 *
 * Two jobs:
 *   1. Removal guard. Removing the LAST anchor while Add On lines remain warns
 *      first, then clears the anchor and every Add On line in ONE cart call.
 *   2. Load audit. A cart found already in the invalid state (built via variant
 *      permalinks, a stale session, a back button) has its Add On lines stripped
 *      silently.
 *
 * Surfaces: it listens on document in the CAPTURE phase, so it runs before the
 * per-button handlers Motion's QtySelector binds, and therefore covers every cart
 * location that uses that markup (cart page, cart drawer, header cart) without
 * touching theme.js.
 *
 * Config comes from window.stAddonAnchor (set by the layout).
 */
(function () {
  'use strict';

  var CFG = window.stAddonAnchor || {};
  var ADDON_TITLE = String(CFG.addonVariantTitle || 'Add On').trim().toLowerCase();
  var WARNING = CFG.warning || 'Removing this will also remove your discounted add-ons.';
  var AUDIT_ON_LOAD = CFG.auditOnLoad !== false;
  var ROOT = ((window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/').replace(/\/+$/, '');

  var SEL_MINUS = '.js-qty__adjust--minus';
  var SEL_INPUT = '.js-qty__num';

  function isAddon(line) {
    return String(line && line.variant_title || '').trim().toLowerCase() === ADDON_TITLE;
  }

  function classify(cart) {
    var items = (cart && cart.items) || [];
    var addons = [], anchors = [];
    items.forEach(function (i) { (isAddon(i) ? addons : anchors).push(i); });
    return { items: items, addons: addons, anchors: anchors };
  }

  function getCart() {
    return fetch(ROOT + '/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); });
  }

  /* One call, many lines: every key set to 0 in a single /cart/update.js. */
  function removeKeys(keys) {
    var updates = {};
    keys.forEach(function (k) { updates[k] = 0; });
    return fetch(ROOT + '/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ updates: updates })
    }).then(function (r) { return r.json(); });
  }

  /* Re-render in place. 'cart:build' is Motion's own rebuild hook, so the cart
     page and drawer redraw from the server (including the empty state) without a
     navigation. 'preset-packs:added' refreshes our own bars and offer logic. */
  function rerender() {
    document.dispatchEvent(new CustomEvent('cart:build'));
    document.dispatchEvent(new CustomEvent('preset-packs:added'));
  }

  /* ---- confirm dialog: themed <dialog>, native confirm() as the fallback ---- */
  function ask(message) {
    return new Promise(function (resolve) {
      var dlg;
      try { dlg = document.createElement('dialog'); } catch (e) { dlg = null; }
      if (!dlg || typeof dlg.showModal !== 'function') { resolve(window.confirm(message)); return; }

      dlg.className = 'st-anchor-dialog';
      dlg.innerHTML =
        '<p class="st-anchor-dialog__msg"></p>' +
        '<div class="st-anchor-dialog__row">' +
          '<button type="button" data-cancel class="st-anchor-dialog__btn">Keep them</button>' +
          '<button type="button" data-ok class="st-anchor-dialog__btn st-anchor-dialog__btn--go">Remove anyway</button>' +
        '</div>';
      dlg.querySelector('.st-anchor-dialog__msg').textContent = message;

      var style = document.createElement('style');
      style.textContent =
        '.st-anchor-dialog{border:1px solid rgba(255,255,255,.14);border-radius:16px;background:#141416;color:#f5f5f7;padding:26px 26px 20px;max-width:400px;width:calc(100vw - 40px);font-family:-apple-system,BlinkMacSystemFont,Inter,"Segoe UI",Helvetica,Arial,sans-serif;box-shadow:0 30px 80px rgba(0,0,0,.6)}' +
        '.st-anchor-dialog::backdrop{background:rgba(0,0,0,.6)}' +
        '.st-anchor-dialog__msg{margin:0 0 22px;font-size:1rem;line-height:1.5}' +
        '.st-anchor-dialog__row{display:flex;gap:10px;justify-content:flex-end;flex-wrap:wrap}' +
        '.st-anchor-dialog__btn{border-radius:980px;padding:11px 20px;font:inherit;font-weight:600;font-size:.92rem;cursor:pointer;border:1px solid rgba(255,255,255,.2);background:transparent;color:#f5f5f7}' +
        '.st-anchor-dialog__btn--go{background:#0071e3;border-color:#0071e3;color:#fff}' +
        '.st-anchor-dialog__btn--go:hover{background:#0a84ff}' +
        '@media (max-width:420px){.st-anchor-dialog__btn{flex:1}}';
      dlg.appendChild(style);

      var done = false;
      function finish(v) {
        if (done) return;
        done = true;
        try { dlg.close(); } catch (e) {}
        if (dlg.parentNode) dlg.parentNode.removeChild(dlg);
        resolve(v);
      }
      dlg.querySelector('[data-ok]').addEventListener('click', function () { finish(true); });
      dlg.querySelector('[data-cancel]').addEventListener('click', function () { finish(false); });
      // Esc closes the dialog: treat that as cancel, never as consent.
      dlg.addEventListener('cancel', function (e) { e.preventDefault(); finish(false); });

      document.body.appendChild(dlg);
      dlg.showModal();
      dlg.querySelector('[data-cancel]').focus();
    });
  }

  /* ---- removal interception ---- */

  var busy = false;

  function keyFromControl(el) {
    var wrap = el.closest('.js-qty__wrapper');
    var input = wrap && wrap.querySelector(SEL_INPUT);
    return { input: input, key: input && input.dataset ? input.dataset.id : null };
  }

  /* Take over the removal entirely: decide, warn if needed, then apply it as one
     operation. Non-removal quantity changes are left to Motion untouched. */
  function handleRemoval(key, input, restoreValue) {
    if (busy) return;
    busy = true;
    getCart()
      .then(function (cart) {
        var c = classify(cart);
        var line = c.items.filter(function (i) { return i.key === key; })[0];
        if (!line) { busy = false; return; }

        var removingAnchor = !isAddon(line);
        var anchorsLeft = c.anchors.filter(function (i) { return i.key !== key; }).length;
        var lastAnchor = removingAnchor && anchorsLeft === 0 && c.addons.length > 0;

        if (!lastAnchor) {
          // Ordinary removal: a non-anchor line, or an anchor with another anchor
          // still standing. No warning, no cascade.
          return removeKeys([key]).then(function () { rerender(); busy = false; });
        }

        return ask(WARNING).then(function (ok) {
          if (!ok) {
            // Cancelled: put the quantity field back the way the customer found it.
            if (input && restoreValue != null) input.value = restoreValue;
            busy = false;
            return;
          }
          var keys = [key].concat(c.addons.map(function (i) { return i.key; }));
          return removeKeys(keys).then(function () { rerender(); busy = false; });
        });
      })
      .catch(function () {
        busy = false;
        if (input && restoreValue != null) input.value = restoreValue;
      });
  }

  // Capture phase: runs before QtySelector's own click handler on the button.
  document.addEventListener('click', function (e) {
    var btn = e.target && e.target.closest && e.target.closest(SEL_MINUS);
    if (!btn) return;
    var found = keyFromControl(btn);
    if (!found.key) return;
    var current = parseInt(found.input.value, 10);
    if (isNaN(current) || current > 1) return;   // a decrement, not a removal
    e.preventDefault();
    e.stopPropagation();
    handleRemoval(found.key, found.input, String(current));
  }, true);

  // Typing 0 (or clearing the field) is the other way to remove a line.
  document.addEventListener('change', function (e) {
    var input = e.target;
    if (!input || !input.matches || !input.matches(SEL_INPUT)) return;
    var next = parseInt(input.value, 10);
    if (!isNaN(next) && next > 0) return;        // still a real quantity
    var key = input.dataset ? input.dataset.id : null;
    if (!key) return;
    e.preventDefault();
    e.stopPropagation();
    handleRemoval(key, input, input.defaultValue || '1');
  }, true);

  /* ---- load audit: silently strip orphaned Add On lines ---- */
  function audit() {
    return getCart().then(function (cart) {
      var c = classify(cart);
      if (!c.addons.length || c.anchors.length) return false;
      return removeKeys(c.addons.map(function (i) { return i.key; })).then(function () {
        rerender();
        return true;
      });
    }).catch(function () { return false; });
  }

  if (AUDIT_ON_LOAD) {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', audit);
    else audit();
  }

  // Re-audit after anything else changes the cart (bundle-rules swaps, add-ons
  // added on this page), so the invalid state can never survive an interaction.
  document.addEventListener('cart:updated', function () { if (!busy) audit(); });

  window.stAddonAnchorAudit = audit;
})();
